import { Card } from "@/components/ui/card";
import { ChevronRightIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import Reveal from "@/components/Motion/Reveal";
import { motion } from "framer-motion";
import Proof from "@/components/proofs/Proof";

export default function HeroSection({ title, subtitle }) {
  const navigate = useNavigate();
  return (
    <div className=" lg:py-0  md:px-10   relative z-50">
      <div className="flex lg:pt-10  md:max-h-[120vh]  flex-col items-center md:flex-row w-full">
        <div
          id="hero-left"
          className="xl:w-7/12 md:w-9/12  flex mt-[1rem] md:mt-[7rem] flex-col p-2"
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, x: -20 },
              show: {
                opacity: 1,
                x: 0,
                transition: {
                  staggerChildren: 0.25,
                  duration: 0.5,
                  ease: "easeOut",
                },
              },
            }}
            initial="hidden"
            animate="show"
            className="flex flex-col translate-x-2  md:translate-x-0 text-center md:text-left"
          >
            <motion.h1
              variants={{
                hidden: { opacity: 0, x: -20 },
                show: { opacity: 1, x: 0, transition: { duration: 0.5 } },
              }}
              className="text-2xl md:text-4xl lg:text-5xl lg:leading-snug font-bold font-serif text-dark-100 relative py-2 "
            >
              {title || "Ihr Immobilienmakler in Eschweiler & Umgebung"}
            </motion.h1>
            <motion.p
              variants={{
                hidden: { opacity: 0, x: -20 },
                show: { opacity: 1, x: 0, transition: { duration: 0.4 } },
              }}
              className="text-lg leading-tight xl:w-10/12 md:text-2xl font-medium text-[#1f2e4b]/90 font-sans py-1"
            >
              {subtitle ||
                "Seit 1978 an Ihrer Seite – wenn Sie Ihre Immobilie bewerten, sicher verkaufen oder Ihr neues Zuhause finden möchten."}
            </motion.p>
          </motion.div>
          <motion.div className="w-fit pt-3 max-w-[40rem]  rounded-none">
            <div className="flex flex-col md:flex-row pb-4 md:py-4 gap-2 mt-10 md:mt-5 md:text-center">
              <Reveal delay={0.1}>
                <Card
                  data-umami-event="landingpage_hero_card_bewerten"
                  onClick={() => {
                    navigate("/bewerten");
                    umami.track(`landingpage_hero_card_bewerten`, "click");
                  }}
                  className="bg-prime-50 md:bg-primary-foreground-100 px-3 relative h-[5rem] md:h-40 w-full md:w-52 md:shadow-none border-gray-300 flex flex-row md:flex-col justify-between gap-6 md:gap-0 md:justify-center items-center border-none shadow-md  cursor-pointer hover:bg-prime-100 rounded-xl  md:rounded-md transition-all duration-300"
                >
                  <img
                    className="md:size-12 w-10 md:pt-2 "
                    src="/Icons/header-bewertung.svg"
                    alt="Kostenlose Immobilienbewertung online"
                  />
                  <p className="md:text-lg text-prime-950 py-2 font-sans font-medium ">
                    Immobilie
                    <br className="hidden md:block" /> bewerten
                  </p>
                  <ChevronRightIcon className="md:hidden md:size-7 stroke-prime-800/70" />
                </Card>
              </Reveal>
              <Reveal delay={0.2}>
                <Card
                  data-umami-event="landingpage_hero_card_verkaufen"
                  onClick={(e) => {
                    navigate("/verkaufen");
                    umami.track(`landingpage_hero_card_verkaufen`, "click");
                  }}
                  className="bg-prime-50 md:bg-primary-foreground-100 px-3 relative h-[5rem] md:h-40 w-full md:w-52 md:shadow-none border-gray-300 flex flex-row md:flex-col justify-between gap-6 md:gap-0 md:justify-center border-none shadow-md items-center cursor-pointer hover:bg-prime-100 rounded-xl  md:rounded-md transition-all duration-300"
                >
                  {/* <Badge className="hidden font-sans absolute left-2 to">
                  Großes Angebot
                </Badge> */}
                  <img
                    className="md:size-12 w-10 md:pt-2"
                    src="/Icons/header-verkauf.svg"
                    alt="Verkaufen sie Ihre Immobilie mit uns"
                  />
                  <p className="md:text-lg text-prime-950 py-2 font-sans font-medium ">
                    Immobilie
                    <br className="hidden md:block" /> verkaufen
                  </p>
                  <ChevronRightIcon className="md:hidden md:size-7 stroke-prime-800/70" />
                </Card>
              </Reveal>
              <Reveal delay={0.3}>
                <Card
                  onClick={() => {
                    navigate("/immobilien");
                    umami.track(`landingpage_hero_card_suche`, "click");
                  }}
                  className="bg-prime-50 md:bg-primary-foreground-100 px-3 relative h-[5rem] md:h-40 w-full md:w-52 md:shadow-none border-gray-300 flex flex-row md:flex-col justify-between gap-6 md:gap-0 md:justify-center items-center cursor-pointer hover:bg-prime-100 rounded-xl border-none shadow-md md:rounded-md transition-all duration-300"
                >
                  <img
                    className="md:size-12  w-10 md:pt-2"
                    src="/Icons/header-angebot.svg"
                    alt="Finden Sie ihre Wunschimmobilie"
                  />
                  <p className="md:text-lg text-prime-950 py-2 font-sans font-medium ">
                    Immobilie
                    <br className="hidden md:block" /> finden
                  </p>
                  <ChevronRightIcon className="md:hidden md:size-7 stroke-prime-800/70" />
                </Card>
              </Reveal>
            </div>

            <Card className="bg-gray-50/60 shadow-none md:h-fit w-full rounded-xl border-none md:rounded-md p-2">
              <p className="font-sans flex items-center gap-1 text-prime-950 ">
                <InfoCircledIcon className="size-5 text-prime-950" /> Oder rufen
                Sie uns für eine kostenlose Beratung an unter{" "}
                <span className="text-prime-800 font-medium">
                  <a href="tel:02403883170">+49 2403 - 883170</a>
                </span>
              </p>
            </Card>
          </motion.div>

          <div className="p-2 pt-10 md:pt-5  w-full md:w-fit ">
            <Proof className="md:hidden" text="gray" />
            <Proof className="hidden md:flex z-30" text="gray-100" />
          </div>
        </div>
        <div
          id="hero-right"
          className="hidden md:flex flex-col items-center p-2 lg:pl-16 mt-10 md:mt-0"
        ></div>
      </div>
    </div>
  );
}
